/** @jsx CanvaSX */

import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import { Rect } from '@lib/canvasx/components/Rect';
import { Text } from '@lib/canvasx/components/Text';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { FaIcon } from '../canvasx/FaIcon';

import ControlBase from '../fabric/ControlBase';
import { fabric } from 'fabric';
import type CanvaSXBehaviors from '../objects/CanvaSXBehaviors';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { type IDataStoreRecordBehaviors } from '../collaboration/DataStoreRecordBehaviors';
import type VotingSessionWidget from '../widgets/VotingSessionWidget';
import { canvasCssValue } from '@lib/canvasx/util';
import { objectType } from '../utils/objects';
import { type CanvaSXObjectState } from './ControlCanvasXBehaviors';
import { pointInRect } from '../utils/geometry';
import { VotingSessionType } from '../widgets/VotingSessionType';
import { type RecordId } from '../shared-types';

type WhiteboardObject = fabric.Object &
  CanvaSXBehaviors &
  IDataStoreRecordBehaviors;

type VoteControlObjectState = CanvaSXObjectState & {
  textWidth: number;
  resultsWidth: number;
  animationValue: number;
};

class VoteControl extends ControlBase {
  actionName = 'vote';
  minDimension = 0; // Disable control hiding
  sizeX = 0;
  sizeY = 0;
  left = 0;
  top = 0;
  cursorStyle = 'auto';

  initialObjectState(): VoteControlObjectState {
    return {
      ...super.initialObjectState(),
      animationValue: 0,
      textWidth: 0,
      resultsWidth: 0,
    };
  }

  getState(
    fabricObject: WhiteboardObject
  ): ReturnType<typeof this.initialObjectState> {
    return super.getState(fabricObject) as VoteControlObjectState;
  }

  getVotingWidget(
    fabricObject: WhiteboardObject
  ): VotingSessionWidget | undefined {
    const widgetsController = fabricObject.controller.widgetsController;
    return widgetsController?.votingSessionWidget;
  }

  onVote(type: 'add' | 'subtract', fabricObject: WhiteboardObject) {
    const widget = this.getVotingWidget(fabricObject);
    const { userId } = fabricObject.controller.state;

    if (widget && userId) {
      if (type === 'add') {
        if (this.canVoteForObject(fabricObject)) {
          widget.voteForObject(fabricObject.uniqueId(), userId);
          fabricObject.canvas.requestRenderAll();
          window.AhaAnalytics?.track(
            'Feature-7236747227355825679',
            'User adds vote',
            {
              'Object type': objectType(fabricObject),
            }
          );
        }
      } else {
        if (this.canUnvoteForObject(fabricObject)) {
          widget.unvoteForObject(fabricObject.uniqueId(), userId);
          fabricObject.canvas.requestRenderAll();
          window.AhaAnalytics?.track(
            'Feature-7236747227355825679',
            'User removes vote',
            {
              'Object type': objectType(fabricObject),
            }
          );
        }
      }
    }
  }

  onMouseOver(eventData: MouseEvent, fabricObject: WhiteboardObject): void {
    if (
      !this.canVoteForObject(fabricObject) &&
      !this.canUnvoteForObject(fabricObject)
    ) {
      return;
    }

    this.animate(fabricObject, 'in');
    super.onMouseOver(eventData, fabricObject);
  }

  onMouseOut(eventData: MouseEvent, fabricObject: WhiteboardObject): void {
    this.animate(fabricObject, 'out');
    super.onMouseOut(eventData, fabricObject);
  }

  getVisibility(fabricObject: WhiteboardObject, key: string): boolean {
    const widget = this.getVotingWidget(fabricObject);
    if (!widget) {
      return false;
    }

    if (!widget.isVotable(fabricObject.uniqueId())) {
      return false;
    }

    if (fabricObject.canvas.getActiveObject() === fabricObject) {
      return false;
    }

    // Hide voting controls if the object is too small to see.
    const zoom = fabricObject.canvas.getZoom();
    if (
      fabricObject.width * fabricObject.scaleX * zoom < 20 &&
      fabricObject.height * fabricObject.scaleY * zoom < 20
    ) {
      return false;
    }

    if (!super.getVisibility(fabricObject, key)) return false;

    return true;
  }

  private animate(fabricObject: WhiteboardObject, name: string): void {
    const state = this.getState(fabricObject);
    let props = {};
    if (name === 'in') {
      props = { startValue: state.animationValue, endValue: 1 };
    } else if (name === 'out') {
      props = { startValue: state.animationValue, endValue: 0 };
    }

    fabric.util.animate({
      duration: 100,
      easing: fabric.util.ease.easeInSine,
      onChange: value => {
        state.animationValue = value;
        fabricObject.canvas.requestRenderAll();
      },
      ...props,
    });
  }

  isActive(fabricObject: WhiteboardObject): boolean {
    return (
      this.canVoteForObject(fabricObject) ||
      this.canUnvoteForObject(fabricObject)
    );
  }

  canVoteForObject(fabricObject: WhiteboardObject): boolean {
    const widget = this.getVotingWidget(fabricObject);
    if (!widget) {
      return false;
    }
    if (widget.sessionType === VotingSessionType.Poker) {
      return false;
    }

    const { userId } = fabricObject.controller.state;
    if (userId) {
      return widget.canVoteForObject(fabricObject.uniqueId(), userId);
    }
    return false;
  }

  canUnvoteForObject(fabricObject: WhiteboardObject): boolean {
    const widget = this.getVotingWidget(fabricObject);
    if (!widget) {
      return false;
    }
    if (widget.sessionType === VotingSessionType.Poker) {
      return false;
    }

    const { userId } = fabricObject.controller.state;
    if (userId) {
      return widget.canUnvoteForObject(fabricObject.uniqueId(), userId);
    }
    return false;
  }

  containsPoint(
    point: { x: number; y: number },
    fabricObject: WhiteboardObject
  ): boolean {
    const topRect = this.getMountedObjectRect('top-row', fabricObject);
    const bottomRect = this.getMountedObjectRect('bottom-row', fabricObject);

    return (
      Boolean(topRect && pointInRect(point, topRect)) ||
      Boolean(bottomRect && pointInRect(point, bottomRect))
    );
  }

  getDisplayedValue(
    widget: VotingSessionWidget,
    objectId: string,
    userId: RecordId | null
  ): string {
    let value: string;
    if (
      widget.sessionType === VotingSessionType.Poker &&
      widget.pokerFinalScores
    ) {
      value = widget.pokerFinalScores[objectId];
    } else {
      if (widget.isFinished) {
        value = widget.votesForObject(objectId).toString();
      } else {
        value = widget.userVotesForObject(objectId, userId).toString();
      }
    }
    return value;
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: any,
    fabricObject: WhiteboardObject
  ): void {
    const widget = this.getVotingWidget(fabricObject);
    if (!widget) {
      return;
    }
    const objectId = fabricObject.uniqueId();
    ctx.save();
    const state = this.getState(fabricObject);
    const { userId } = fabricObject.controller.state;
    const displayedValue = this.getDisplayedValue(widget, objectId, userId);

    const isPlanningPoker = widget.sessionType === VotingSessionType.Poker;

    // Planning poker doesn't have winners, just finalised scores
    const winner = isPlanningPoker ? false : widget.isWinner(objectId);

    const borderRadius = 4;
    const buttonPadding = state.animationValue * 9;
    const buttonTotalWidth =
      (state.animationValue + 0.01) * (27 - borderRadius); // Have to add a tiny bit because 0 is treated special
    const vertPadding = 7;
    const minTextWidth = 33;
    const extraTextPadding = borderRadius * state.animationValue;

    const calculatedTextWidth =
      Math.max(
        minTextWidth - borderRadius * 2,
        state.textWidth ?? 0,
        state.resultsWidth ?? 0
      ) +
      extraTextPadding * 2;

    // Width to help center the controls. Do not use
    // setRenderedDimensions values that change during animation, or
    // it will jiggle.
    const width = borderRadius * 2 + buttonTotalWidth * 2 + calculatedTextWidth;
    const topRect =
      this.getMountedObjectRect('top-row', fabricObject) ||
      this.getMountedObjectRect('bottom-row', fabricObject);

    this.left = left - width / 2;
    this.top = top - (topRect?.height ?? 0) / 2;

    state.mountedObjects = {};

    withContext(
      {
        canvas: fabricObject.canvas,
        ctx,
        object: state,
        scale: 1,
      },
      ctx => {
        ctx.translate(this.left, this.top);
        const fill = canvasCssValue('--theme-button-primary', '#0073CF');
        const fillHover = canvasCssValue(
          '--theme-button-primary-hovered',
          '#004582'
        );
        const fillDisabled = canvasCssValue('--theme-accent-text', '#B5B5B5');
        const primaryText = canvasCssValue('--theme-primary-text', '#333333');
        const invertedText = canvasCssValue(
          '--theme-inverted-primary-text',
          '#FFFFFF'
        );

        let fillText = fill;
        let votesTooltip: string | undefined;
        const addProps = { fill, opacity: 1 };
        const subtractProps = { fill, opacity: 1 };
        const key = state.prevMountedObjectKey;

        if (state.animationValue > 0) {
          if (!this.canUnvoteForObject(fabricObject)) {
            subtractProps.opacity = 0.2;
          } else if (key === 'subtract') {
            subtractProps.fill = fillHover;
          }

          if (!this.canVoteForObject(fabricObject)) {
            addProps.opacity = 0.2;
          } else if (key === 'add') {
            addProps.fill = fillHover;
          }
        }

        if (!this.isActive(fabricObject)) {
          fillText = fillDisabled;
          subtractProps.opacity = 0.2;
          subtractProps.fill = fillText;
          addProps.opacity = 0.2;
          addProps.fill = fillText;

          if (!widget.isFinished && !isPlanningPoker) {
            votesTooltip = 'You have no votes left';
          }
        }

        renderElement(
          <FlexColumn
            horizontalAlignment='center'
            gap={4}
            setRenderedDimensions={dimensions => {
              if (
                this.sizeX !== dimensions.width ||
                this.sizeY !== dimensions.height
              ) {
                this.sizeX = dimensions.width;
                this.sizeY = dimensions.height;
              }
            }}
          >
            {!widget.isFinished && (
              <FlexRow
                key='top-row'
                tooltip={votesTooltip}
                verticalAlignment='stretch'
                borderRadius={borderRadius}
                fill={fillText}
                wrap={false}
              >
                <Rect
                  key='subtract'
                  borderRadius={{
                    topLeft: borderRadius,
                    bottomLeft: borderRadius,
                  }}
                  width={borderRadius + buttonTotalWidth}
                  fill={subtractProps.fill}
                  onClick={
                    state.animationValue > 0
                      ? () => {
                          this.onVote('subtract', fabricObject);
                        }
                      : undefined
                  }
                >
                  <FlexRow
                    minHeight={32}
                    verticalAlignment='center'
                    padding={{ horizontal: buttonPadding }}
                  >
                    <FaIcon
                      icon='minus'
                      style='solid'
                      size={12}
                      color={invertedText}
                      opacity={subtractProps.opacity}
                      maxWidth={buttonTotalWidth}
                    />
                  </FlexRow>
                </Rect>
                <Rect fill={fillText}>
                  <FlexRow minHeight={32} verticalAlignment='center'>
                    <FlexColumn
                      horizontalAlignment='center'
                      width={calculatedTextWidth}
                    >
                      <FlexColumn
                        setRenderedDimensions={dimensions => {
                          state.textWidth = dimensions.width;
                        }}
                        padding={{
                          top: 1,
                        }}
                      >
                        <Text
                          color={
                            fillText === fillDisabled
                              ? primaryText
                              : invertedText
                          }
                          size={14}
                          face='Inter'
                          weight={600}
                        >
                          {displayedValue}
                        </Text>
                      </FlexColumn>
                    </FlexColumn>
                  </FlexRow>
                </Rect>
                <Rect
                  key='add'
                  onClick={
                    state.animationValue > 0
                      ? () => {
                          this.onVote('add', fabricObject);
                        }
                      : undefined
                  }
                  fill={addProps.fill}
                  borderRadius={{
                    topRight: borderRadius,
                    bottomRight: borderRadius,
                  }}
                  width={borderRadius + buttonTotalWidth}
                >
                  <FlexRow
                    minHeight={32}
                    verticalAlignment='center'
                    padding={{ horizontal: buttonPadding }}
                  >
                    <FaIcon
                      icon='plus'
                      style='solid'
                      size={12}
                      color={invertedText}
                      opacity={addProps.opacity}
                      maxWidth={buttonTotalWidth}
                    />
                  </FlexRow>
                </Rect>
              </FlexRow>
            )}
            {widget.isFinished && (
              <FlexColumn
                key='bottom-row'
                tooltip='Total votes'
                fill={canvasCssValue('--theme-inverted-background', '#333333')}
                borderRadius={borderRadius}
                horizontalAlignment='center'
                padding={{
                  vertical: vertPadding,
                }}
                minWidth={minTextWidth}
                setRenderedDimensions={dimensions => {
                  state.resultsWidth = dimensions.width - borderRadius * 2;
                }}
              >
                <Text color={invertedText} size={12} face='Inter'>
                  {displayedValue}
                </Text>
                {winner && (
                  <Rect padding={{ top: vertPadding }}>
                    <FaIcon
                      icon='stars'
                      style='solid'
                      size={12}
                      color={canvasCssValue('--aha-gold-400', '#FFC655')}
                    />
                  </Rect>
                )}
              </FlexColumn>
            )}
          </FlexColumn>
        );
      }
    );

    ctx.restore();

    const updatedTopRect =
      this.getMountedObjectRect('top-row', fabricObject) ||
      this.getMountedObjectRect('bottom-row', fabricObject);
    if (topRect?.height !== updatedTopRect?.height) {
      // We use height for positioning. If it changes, we need to
      // rerender the object at the new position.
      fabricObject.canvas.requestRenderAll();
    }
  }
}

export default VoteControl;
